import { GameManager } from "../../GameManager";
import { HUD } from "./HUD";
import { GUI } from "./GUI";
import { BoardContainer } from "./BoardContainer";
import { GameConstants } from "../../GameConstants";
import { BoardManager } from "./BoardManger";
import { ClearedBoardLayer } from "./ClearedBoardLayer";
import { InfoLayer } from "./InfoLayer";
import { GameVars } from "../../GameVars";
import { TitleLayer } from "./TitleLayer";
import { PauseLayer } from "./PauseLayer";
import { AudioManager } from "../../AudioManager";
import { RestoredSessionLayer } from "./RestoredSessionLayer";
import { UndoContainer } from "./UndoContainer";
import * as Mahjong from "@ravalmatic/mahjong-engine";
import { BoardClearedAnimation } from "./level-cleared-animation/BoardClearedAnimation";

export class BoardScene extends Phaser.Scene {

    public static currentInstance: BoardScene;

    public infoLayer: InfoLayer;
    public gui: GUI;
    public hud: HUD;

    private boardContainer: BoardContainer;
    private restoredSessionLayer: RestoredSessionLayer;
    private titleLayer: TitleLayer; 
    private pauseLayer: PauseLayer;
    private undoContainer: UndoContainer;
    private boardClearedAnimation: BoardClearedAnimation;

    constructor() {

        super("BoardScene");
        
        BoardScene.currentInstance = this;
    }

    public create(): void {

        GameManager.setCurrentScene(this);

        this.infoLayer = null;
        this.pauseLayer = null;
        this.boardClearedAnimation = null;

        const background = this.add.image(GameConstants.GAME_WIDTH / 2, GameConstants.GAME_HEIGHT, "background");
        background.setOrigin(.5, 1);
        background.scaleX = GameVars.scaleX;

        if (GameVars.isMobile) {

            const aspectRatio = window.innerWidth / window.innerHeight;

            if (aspectRatio > 2.2) {
                background.scaleX = 1.25 * GameVars.scaleX;
                background.scaleY = 1.25;
            }
        }

        this.boardContainer = new BoardContainer(this);
        this.add.existing(this.boardContainer);

        this.undoContainer = new UndoContainer(this);
        this.add.existing(this.undoContainer);

        this.hud = new HUD(this);
        this.add.existing(this.hud);

        this.gui = new GUI(this);
        this.add.existing(this.gui);

        if (!this.anims.exists("match_fx")) {
            this.anims.create({key: "match_fx", frames: this.anims.generateFrameNames("texture_atlas_4", {prefix: "fx_match_", start: 0, end: 20, zeroPad: 2, suffix: ""}), frameRate: 45, hideOnComplete: true});
        }

        BoardManager.init();  
    }

    public update(): void {

        BoardManager.update();
        
        this.boardContainer.update();
        this.hud.update();
        this.gui.update();

        if (this.boardClearedAnimation) {
            this.boardClearedAnimation.update();
        }
    }

    public startGame(): void {

        if (GameVars.restoredSession) {
            
            this.restoredSessionLayer = new RestoredSessionLayer(this);
            this.add.existing(this.restoredSessionLayer);

        } else {
            
            if (!GameVars.levelReset) {
                this.titleLayer = new TitleLayer(this);
                this.add.existing(this.titleLayer);
            }
        }

        this.boardContainer.startGame(true);
        this.undoContainer.startGame();
        this.hud.startGame(); 
        this.gui.startGame();

        AudioManager.playSound("soundtrack_mahjong", true);
    }

    public undo(tileA: Mahjong.TileData, tileB: Mahjong.TileData): void {   

        this.boardContainer.undo(tileA, tileB);
        this.hud.undo();
        this.undoContainer.undo();
    }

    public onGamePausedResumed(): void {

        this.hud.onGamePausedResumed();
        this.boardContainer.onGamePausedResumed();

        if (GameVars.paused) {

            this.pauseLayer = new PauseLayer(this);
            this.add.existing(this.pauseLayer);

            AudioManager.setLoopVolume(1, .3);
        } else {

            this.pauseLayer.destroy();
            this.pauseLayer = null;
            
            AudioManager.setLoopVolume(.3, 1);
        }
    }

    public onMatch(tileA: Mahjong.TileData, tileB: Mahjong.TileData, scoreData: {score: number, deltaSore: number, combo: number}): void {   

        this.boardContainer.onMatch(tileA, tileB, scoreData);
        this.gui.onMatch();
        this.hud.onMatch(scoreData);
    }

    public onBoardCleared(): void {

        this.gui.onBoardCleared();

        this.boardContainer.onBoardCleared();

        this.boardClearedAnimation = new BoardClearedAnimation(this);
        this.add.existing(this.boardClearedAnimation);

        const clearedBoardLayer = new ClearedBoardLayer(this);
        this.add.existing(clearedBoardLayer);

        this.time.delayedCall(1000, () => {
    
            AudioManager.playSound("board_cleared", true);
            
        }, [], this);  
    }

    public onShuffle(): void {

        this.boardContainer.onShuffle();

        this.hud.onShuffle();
    }

    public showInfo(): void {

        this.infoLayer = new InfoLayer(this);
        this.add.existing(this.infoLayer);

        this.boardContainer.onInfoShown();
    }

    public hideInfo(): void {

        this.infoLayer.destroy();
        this.infoLayer = null;

        this.boardContainer.onInfoHidden();
    }

    public onHint(): void {

        this.boardContainer.onHint();

        this.gui.onHint();
    }
}
